import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { Link2, Loader2, ChevronDown, ArrowRight } from "lucide-react";
import { toast } from "sonner";

// Calm, on-demand "Related passages" section shown under the reading. Each entry
// is tappable to open a fresh study on that passage.
export default function CrossReferences({ session }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(session?.cross_references || null);
  const [opening, setOpening] = useState("");

  const load = async () => {
    setOpen(true);
    if (items || loading) return;
    setLoading(true);
    try {
      const { data } = await api.post(`/study/sessions/${session.id}/cross-references`);
      setItems(data.related || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load related passages");
      setOpen(false);
    }
    setLoading(false);
  };

  const openPassage = async (ref) => {
    setOpening(ref);
    try {
      const { data } = await api.post("/study/start", {
        input: ref,
        mode: "passage",
        depth: session.depth || "intermediate",
        translation: session.translation || "niv",
      });
      navigate(`/study/${data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't open that passage");
      setOpening("");
    }
  };

  return (
    <section data-testid="cross-references" className="rounded-2xl bg-[#0E131F] border border-[#1E273D] overflow-hidden">
      <button
        data-testid="cross-references-toggle"
        onClick={() => (open ? setOpen(false) : load())}
        className="w-full flex items-center justify-between px-6 py-4 text-left"
      >
        <span className="flex items-center gap-2 text-xs font-medium uppercase tracking-widest text-[#00E5FF]">
          <Link2 className="w-4 h-4" strokeWidth={1.5} /> Related passages
        </span>
        <ChevronDown
          className={`w-4 h-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={1.5}
        />
      </button>

      {open && (
        <div className="px-6 pb-6 space-y-3">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" strokeWidth={1.5} /> Finding passages that connect…
            </div>
          )}
          {!loading && items && items.length === 0 && (
            <p className="text-sm text-slate-500">No related passages found.</p>
          )}
          {!loading &&
            (items || []).map((r, i) => (
              <button
                key={i}
                data-testid={`cross-ref-${i}`}
                onClick={() => openPassage(r.reference)}
                disabled={!!opening}
                className="group w-full text-left rounded-xl border border-[#1E273D] hover:border-[#00E5FF]/40 hover:bg-[#10222A]/30 p-4 transition-colors disabled:opacity-60"
              >
                <div className="flex items-center justify-between">
                  <span className="text-lg text-slate-200">{r.reference}</span>
                  {opening === r.reference ? (
                    <Loader2 className="w-4 h-4 animate-spin text-[#00E5FF]" strokeWidth={1.5} />
                  ) : (
                    <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[#00E5FF] transition-colors" strokeWidth={1.5} />
                  )}
                </div>
                {r.connection && <p className="text-sm text-slate-400 mt-1 leading-relaxed">{r.connection}</p>}
              </button>
            ))}
          <p className="text-[11px] text-slate-500 pt-1 leading-relaxed">
            Suggested connections to explore in the text yourself — OpenWord AI is a study tool that points to Scripture.
          </p>
        </div>
      )}
    </section>
  );
}
